<template>
  <div class="instructors-page">
    <!-- Hero Section -->
    <div class="instructors-hero">
      <div class="hero-content">
        <div class="hero-icon">
          <div class="icon-glow"></div>
          <div class="icon-inner">
            <i class="fas fa-chalkboard-teacher"></i>
          </div>
        </div>
        <h1 class="hero-title">{{ $t("instructors.title") || "المحاضرون" }}</h1>
        <p class="hero-subtitle">
          {{
            $t("instructors.subtitle") ||
            "تعرف على نخبة المحاضرين في منصة أكاديميا"
          }}
        </p>
      </div>
      <div class="hero-decoration">
        <div class="circle circle-1"></div>
        <div class="circle circle-2"></div>
        <div class="circle circle-3"></div>
        <div class="hero-particles">
          <span></span><span></span><span></span><span></span><span></span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="instructors-container">
      <!-- Search Section -->
      <div class="search-section">
        <div class="search-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="
              $t('instructors.search_placeholder') || 'ابحث عن محاضر...'
            "
            class="search-input"
            @input="handleSearch"
          />
          <button
            v-if="searchQuery"
            class="clear-search-btn"
            @click="clearSearch"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Stats Bar -->
      <div class="stats-bar" v-if="!isLoading && instructors.length > 0">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-users"></i>
          </div>
          <div class="stat-info">
            <span class="stat-number">{{
              meta.total || instructors.length
            }}</span>
            <span class="stat-label">{{
              $t("instructors.total_instructors") || "إجمالي المحاضرين"
            }}</span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">
          {{ $t("common.loading") || "جاري التحميل..." }}
        </p>
      </div>

      <!-- Empty State -->
      <div v-else-if="instructors.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-user-slash"></i>
        </div>
        <h3>{{ $t("instructors.no_instructors") || "لا يوجد محاضرون" }}</h3>
        <p>
          {{
            $t("instructors.no_instructors_desc") ||
            "لم يتم العثور على محاضرين حالياً"
          }}
        </p>
      </div>

      <!-- Instructors Grid -->
      <div v-else class="instructors-grid">
        <div
          v-for="(instructor, index) in instructors"
          :key="instructor.id"
          class="instructor-card"
          :style="{ animationDelay: `${index * 0.08}s` }"
        >
          <div class="card-header-bg"></div>

          <div class="instructor-avatar-section">
            <div class="avatar-glow"></div>
            <div class="instructor-avatar">
              <img
                :src="instructor.user_img"
                :alt="instructor.name"
                @error="setDefaultImage"
              />
            </div>
            <div class="verified-badge" v-if="instructor.is_verified !== false">
              <i class="fas fa-check"></i>
            </div>
          </div>

          <div class="instructor-info">
            <h4 class="instructor-name">{{ instructor.name }}</h4>

            <div class="instructor-badge">
              <i class="fas fa-graduation-cap"></i>
              <span>{{
                $t("instructors.platform_badge") || "محاضر معتمد"
              }}</span>
            </div>

            <div class="instructor-stats">
              <div class="stat-item" v-if="instructor.courses_count">
                <i class="fas fa-book"></i>
                <span
                  >{{ instructor.courses_count }}
                  {{ $t("instructors.courses") || "دورة" }}</span
                >
              </div>
              <div class="stat-item" v-if="instructor.students_count">
                <i class="fas fa-users"></i>
                <span
                  >{{ instructor.students_count }}
                  {{ $t("instructors.students") || "طالب" }}</span
                >
              </div>
            </div>

            <div class="instructor-rating">
              <div class="stars">
                <i class="fas fa-star" v-for="n in 5" :key="n"></i>
              </div>
            </div>
          </div>

          <router-link
            :to="{ name: 'InstructorProfile', query: { id: instructor.id } }"
            class="view-profile-btn"
          >
            <span>{{
              $t("instructors.view_profile") || "عرض الملف الشخصي"
            }}</span>
            <i class="fas fa-arrow-left"></i>
          </router-link>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="
          pagination.links &&
          pagination.links.length > 3 &&
          instructors.length > 0
        "
        class="pagination-section"
      >
        <div class="pagination-wrapper">
          <button
            class="pagination-btn prev-btn"
            :class="{ disabled: !pagination.prev }"
            @click="changePage(pagination.prev)"
            :disabled="!pagination.prev"
          >
            <i class="fas fa-chevron-right"></i>
            <span>{{ $t("common.prev") || "السابق" }}</span>
          </button>

          <div class="page-numbers">
            <button
              v-for="link in visiblePageLinks"
              :key="link.label"
              class="page-number"
              :class="{ active: link.active }"
              @click="goToPage(link.label)"
              :disabled="link.active"
            >
              {{ link.label }}
            </button>
          </div>

          <button
            class="pagination-btn next-btn"
            :class="{ disabled: !pagination.next }"
            @click="changePage(pagination.next)"
            :disabled="!pagination.next"
          >
            <span>{{ $t("common.next") || "التالي" }}</span>
            <i class="fas fa-chevron-left"></i>
          </button>
        </div>

        <div class="page-info">
          {{ $t("instructors.page") || "صفحة" }} {{ meta.current_page }}
          {{ $t("instructors.of") || "من" }} {{ meta.last_page }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  name: "InstructorsPage",
  data() {
    return {
      instructors: [],
      defaultImage: "/assets/img/acad-icon.png",
      isLoading: false,
      searchQuery: "",
      searchTimeout: null,
      pagination: {
        links: [],
        prev: null,
        next: null,
      },
      meta: {
        current_page: 1,
        last_page: 1,
        total: 0,
      },
    };
  },
  computed: {
    visiblePageLinks() {
      return this.pagination.links.filter((link) => {
        const label = link?.label;
        return label && (Number(label) || link.active);
      });
    },
  },
  mounted() {
    this.getInstructors(`${apiUrl}/instructors-v2`);
  },
  methods: {
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },
    async getInstructors(url) {
      try {
        this.isLoading = true;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-country-code": window.appRegion?.phone_code || "SA",
          },
        });
        const data = await res.json();

        this.instructors = data.data || [];

        // Convert links to https
        const convertToHttps = (link) =>
          link ? link.replace(/^http:/, "https:") : null;

        if (data.meta?.links) {
          this.pagination.links = data.meta.links.map((linkObj) => ({
            ...linkObj,
            url: convertToHttps(linkObj.url),
          }));
        }

        if (data.links) {
          this.pagination.prev = data.links.prev
            ? apiUrl + "/instructors-v2" + data.links.prev
            : null;
          this.pagination.next = data.links.next
            ? apiUrl + "/instructors-v2" + data.links.next
            : null;
        }

        this.meta = {
          current_page: data.meta?.current_page || 1,
          last_page: data.meta?.last_page || 1,
          total: data.meta?.total || this.instructors.length,
        };
      } catch (err) {
        console.error("Error fetching instructors:", err);
      } finally {
        this.isLoading = false;
      }
    },

    changePage(url) {
      if (!url) return;
      this.getInstructors(url);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    goToPage(pageNumber) {
      const page = parseInt(pageNumber);
      if (isNaN(page) || page === this.meta.current_page) return;
      let url = `${apiUrl}/instructors-v2?page=${page}`;
      if (this.searchQuery.trim()) {
        url += `&search=${encodeURIComponent(this.searchQuery.trim())}`;
      }
      this.getInstructors(url);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    handleSearch() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 400);
    },

    performSearch() {
      let url = `${apiUrl}/instructors-v2`;
      if (this.searchQuery.trim()) {
        url += `?search=${encodeURIComponent(this.searchQuery.trim())}`;
      }
      this.getInstructors(url);
    },

    clearSearch() {
      this.searchQuery = "";
      this.getInstructors(`${apiUrl}/instructors-v2`);
    },
  },
};
</script>

<style scoped>
/* ===== Page Container ===== */
.instructors-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fff5 0%, #e6ffee 100%);
  direction: rtl;
}

/* ===== Hero Section ===== */
.instructors-hero {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008830 100%);
  padding: 48px 24px;
  position: relative;
  overflow: hidden;
  text-align: center;
}

.hero-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-icon {
  position: relative;
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
}

.icon-glow {
  position: absolute;
  inset: -8px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 70%
  );
  border-radius: 50%;
  animation: glow-pulse 2s ease-in-out infinite;
}

.icon-inner {
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.icon-inner i {
  font-size: 42px;
  color: #ffffff;
}

.hero-title {
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hero-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  max-width: 400px;
}

.hero-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -50px;
}

.circle-2 {
  width: 150px;
  height: 150px;
  bottom: -30px;
  left: -30px;
}

.circle-3 {
  width: 80px;
  height: 80px;
  top: 50%;
  left: 20%;
}

.hero-particles span {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: particle-float 4s ease-in-out infinite;
}

.hero-particles span:nth-child(1) {
  left: 10%;
  top: 20%;
  animation-delay: 0s;
}
.hero-particles span:nth-child(2) {
  left: 80%;
  top: 30%;
  animation-delay: 0.5s;
}
.hero-particles span:nth-child(3) {
  left: 20%;
  top: 70%;
  animation-delay: 1s;
}
.hero-particles span:nth-child(4) {
  left: 70%;
  top: 80%;
  animation-delay: 1.5s;
}
.hero-particles span:nth-child(5) {
  left: 50%;
  top: 50%;
  animation-delay: 2s;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes particle-float {
  0%,
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-20px) scale(1.2);
    opacity: 0.8;
  }
}

/* ===== Container ===== */
.instructors-container {
  padding: 32px 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* ===== Search Section ===== */
.search-section {
  margin-bottom: 24px;
}

.search-wrapper {
  position: relative;
  max-width: 500px;
  margin: 0 auto;
}

.search-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
  transition: color 0.3s ease;
}

.search-input {
  width: 100%;
  padding: 16px 50px 16px 50px;
  border: 2px solid #e5e7eb;
  border-radius: 50px;
  font-size: 1rem;
  background: white;
  color: #1f2937;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.search-input:focus {
  outline: none;
  border-color: #00c744;
  box-shadow: 0 4px 20px rgba(0, 199, 68, 0.15);
}

.search-input:focus + .search-icon,
.search-input:focus ~ .search-icon {
  color: #00c744;
}

.search-input::placeholder {
  color: #9ca3af;
}

.clear-search-btn {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}

.clear-search-btn:hover {
  background: #e5e7eb;
  color: #1f2937;
}

/* ===== Stats Bar ===== */
.stats-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.stat-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 24px;
  color: #00c744;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
}

/* ===== Loading State ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  position: relative;
  width: 70px;
  height: 70px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00a83b;
  animation-duration: 1s;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #008f32;
  animation-duration: 0.8s;
}

.loading-text {
  margin-top: 20px;
  color: #00c744;
  font-size: 1rem;
  font-weight: 600;
}

/* ===== Empty State ===== */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.empty-icon i {
  font-size: 48px;
  color: #00c744;
}

.empty-state h3 {
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

/* ===== Instructors Grid ===== */
.instructors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 28px;
}

/* ===== Instructor Card ===== */
.instructor-card {
  background: white;
  border-radius: 24px;
  padding: 0;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  position: relative;
  overflow: hidden;
}

.card-header-bg {
  height: 100px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 24px 24px 50% 50% / 24px 24px 30% 30%;
}

.instructor-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 60px rgba(0, 199, 68, 0.18);
}

/* ===== Avatar Section ===== */
.instructor-avatar-section {
  position: relative;
  margin-top: -60px;
  margin-bottom: 16px;
  z-index: 1;
}

.avatar-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 130px;
  height: 130px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  opacity: 0.15;
  animation: pulse 2s ease-in-out infinite;
}

.instructor-avatar {
  width: 110px;
  height: 110px;
  margin: 0 auto;
  border-radius: 50%;
  padding: 4px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 199, 68, 0.25);
}

.instructor-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
}

.verified-badge {
  position: absolute;
  bottom: 5px;
  right: calc(50% - 70px);
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 199, 68, 0.3);
}

.verified-badge i {
  color: white;
  font-size: 12px;
}

/* ===== Instructor Info ===== */
.instructor-info {
  padding: 0 24px;
}

.instructor-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.instructor-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  padding: 8px 18px;
  border-radius: 25px;
  font-size: 0.85rem;
  color: #00c744;
  font-weight: 600;
  margin-bottom: 16px;
}

.instructor-badge i {
  font-size: 14px;
}

.instructor-stats {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 12px;
}

.instructor-stats .stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #6b7280;
}

.instructor-stats .stat-item i {
  color: #00c744;
  font-size: 14px;
}

.instructor-rating {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.stars {
  display: flex;
  gap: 4px;
}

.stars i {
  color: #fbbf24;
  font-size: 16px;
}

/* ===== View Profile Button ===== */
.view-profile-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: calc(100% - 48px);
  margin: 0 24px 24px;
  padding: 14px 25px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.25);
}

.view-profile-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 199, 68, 0.35);
  color: white;
}

.view-profile-btn i {
  transition: transform 0.3s ease;
}

.view-profile-btn:hover i {
  transform: translateX(-5px);
}

/* ===== Pagination ===== */
.pagination-section {
  margin-top: 48px;
  text-align: center;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  border: 2px solid #00c744;
  border-radius: 50px;
  color: #00c744;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(.disabled) {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #cbd5e0;
  color: #cbd5e0;
}

.page-numbers {
  display: flex;
  gap: 8px;
}

.page-number {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  border-radius: 12px;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
}

.page-number:hover:not(.disabled):not(.active) {
  background: #e8fff1;
  color: #00c744;
}

.page-number.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 5px 15px rgba(0, 199, 68, 0.3);
}

.page-info {
  margin-top: 16px;
  color: #6b7280;
  font-size: 0.9rem;
}

/* ===== Animations ===== */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.15;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.25;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Dark Mode Support ===== */
[data-bs-theme="dark"] .instructors-page,
[data-theme="dark"] .instructors-page,
.dark .instructors-page {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .instructor-card,
[data-theme="dark"] .instructor-card,
.dark .instructor-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .card-header-bg,
[data-theme="dark"] .card-header-bg,
.dark .card-header-bg {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .instructor-name,
[data-theme="dark"] .instructor-name,
.dark .instructor-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .instructor-badge,
[data-theme="dark"] .instructor-badge,
.dark .instructor-badge {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
  color: #4ade80;
}

[data-bs-theme="dark"] .search-input,
[data-theme="dark"] .search-input,
.dark .search-input {
  background: #1e293b;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .search-input:focus,
[data-theme="dark"] .search-input:focus,
.dark .search-input:focus {
  border-color: #4ade80;
  box-shadow: 0 4px 20px rgba(74, 222, 128, 0.15);
}

[data-bs-theme="dark"] .search-input::placeholder,
[data-theme="dark"] .search-input::placeholder,
.dark .search-input::placeholder {
  color: #64748b;
}

[data-bs-theme="dark"] .clear-search-btn,
[data-theme="dark"] .clear-search-btn,
.dark .clear-search-btn {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .clear-search-btn:hover,
[data-theme="dark"] .clear-search-btn:hover,
.dark .clear-search-btn:hover {
  background: #475569;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .stat-card,
[data-theme="dark"] .stat-card,
.dark .stat-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .stat-number,
[data-theme="dark"] .stat-number,
.dark .stat-number {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .stat-icon,
[data-theme="dark"] .stat-icon,
.dark .stat-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .stat-icon i,
[data-theme="dark"] .stat-icon i,
.dark .stat-icon i {
  color: #4ade80;
}

[data-bs-theme="dark"] .empty-state,
[data-theme="dark"] .empty-state,
.dark .empty-state {
  background: #1e293b;
}

[data-bs-theme="dark"] .empty-state h3,
[data-theme="dark"] .empty-state h3,
.dark .empty-state h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .pagination-btn,
[data-theme="dark"] .pagination-btn,
.dark .pagination-btn {
  background: #1e293b;
  border-color: #4ade80;
  color: #4ade80;
}

[data-bs-theme="dark"] .page-number,
[data-theme="dark"] .page-number,
.dark .page-number {
  background: #1e293b;
  color: #94a3b8;
}

[data-bs-theme="dark"] .page-number:hover:not(.disabled):not(.active),
[data-theme="dark"] .page-number:hover:not(.disabled):not(.active),
.dark .page-number:hover:not(.disabled):not(.active) {
  background: #0f172a;
  color: #4ade80;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .instructors-hero {
    padding: 36px 20px;
  }

  .hero-icon {
    width: 80px;
    height: 80px;
  }

  .icon-inner i {
    font-size: 32px;
  }

  .hero-title {
    font-size: 26px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .instructors-container {
    padding: 24px 16px;
  }

  .instructors-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
  }

  .page-numbers {
    order: -1;
  }
}
</style>
